import {
  cloneDeep,
  differenceWith,
  every,
  find,
  findIndex,
  get,
  isEmpty,
  map,
  omit,
  range,
  reject,
  some,
  uniqBy,
} from 'lodash';
import { v1 as uuidv1 } from 'uuid';
// temporary, we will need to handle a different empty answer per question type
import {
  CARRIED_VALUE_PLACEHOLDERS,
  QUESTION_MATRIX_BIPOLAR,
  QUESTION_MATRIX_CONSTANT_SUM,
  QUESTION_MATRIX_MULTIPLE,
  QUESTION_MATRIX_OPEN_ENDED,
  QUESTION_MATRIX_NUMERIC_OPEN_ENDED,
  QUESTION_MATRIX_SINGLE,
  QUESTION_MULTIPLE,
  QUESTION_NUMERIC_OPEN_ENDED,
  QUESTION_OPEN_ENDED,
  QUESTION_RANKING,
  QUESTION_SINGLE,
  QUESTION_SLIDER,
  GROUP_LOOP_MERGE,
  QUESTION_SINGLE_PEEQ,
  QUESTION_MULTIPLE_PEEQ,
} from 'data/nodeTypes';
import {
  isBipolarQuestionType,
  isMatrixAndNotBipolarQuestionType,
  isMatrixMultipleQuestionType,
  isMatrixOpenEndedType,
  isMatrixSingleMultipleQuestionType,
  isOpenEndedOrNumericOpenEndedOrSliderQuestionType,
  isRankingQuestionType,
  isSingleOrMultipleQuestionType,
} from 'utils/nodeTypes';
import {
  canCarryForwardFrom,
  canLoopAndMergeFrom,
  questionAnswerSupportsMedia,
} from 'utils/questionHelpers';
import {
  getEmptyAnswer as getEmptySingleMultipleAnswer,
  isAnswerEmpty as isSingleMultipleAnswerEmpty,
} from 'containers/QuestionnairePage/GenericQuestion/SingleMultipleQuestion/utils';
import { isAnswerEmpty as isEmptyMatrixAnswer } from 'containers/QuestionnairePage/GenericQuestion/MatrixQuestion/utils';
import { ANSWER_NONE_OF_THE_ABOVE } from 'containers/QuestionnairePage/constants';

import {
  getEmptyAttributeValue,
  getEmptyConcept,
  getLoopAndMergeAttributes,
  getLoopAndMergeConceptName,
  getLoopAndMergeConcepts,
  getLoopAndMergeFromQuestionAttribute,
  isLoopAndMergeAttributeBasedOnQuestion,
} from 'containers/QuestionnairePage/QuestionGroup/utils';
import { getInnerContentFromHtmlString } from 'components/FormattedInput/helpers';
import { LOOP_AND_MERGE_ATTRIBUTE_TYPE } from 'containers/QuestionnairePage/QuestionGroup/constants';
import {
  ANSWER_OPTIONS,
  BIPOLAR_LABEL_OPTIONS,
  FORWARD_ANSWER_AS,
} from '../constants';
import {
  createCarriedMatrixAnswer,
  isNewMatrixProp,
  MATRIX_ANSWER_PROP,
  MATRIX_COLUMN_PROP,
  updateCarriedMatrixAnswer,
} from './matrix';

import { getAnswerValueColumnTypesOpenEnded } from '../../../../GenericQuestion/MatrixOpenEndedQuestion/utils';
export const getCarriedValuePlaceHolder = (questionType) => {
  switch (questionType) {
    case QUESTION_OPEN_ENDED:
      return CARRIED_VALUE_PLACEHOLDERS[QUESTION_OPEN_ENDED];
    case QUESTION_NUMERIC_OPEN_ENDED:
      return CARRIED_VALUE_PLACEHOLDERS[QUESTION_NUMERIC_OPEN_ENDED];
    case QUESTION_SLIDER:
      return CARRIED_VALUE_PLACEHOLDERS[QUESTION_SLIDER];
    default:
      return '';
  }
};

export const getBipolarAnswerBodyByRefId = (
  matrixAnswers,
  answerRefId,
  answerLabelSelection,
) => {
  const emptyMatrixAnswer = { beforeLabel: '', afterLabel: '' };
  const relevantMatrixAnswer =
    find(matrixAnswers, ['answerRefId', answerRefId]) || emptyMatrixAnswer;

  return getBipolarAnswerBody(relevantMatrixAnswer, answerLabelSelection);
};

export const getBipolarAnswerBody = (matrixAnswer, answerLabelSelection) => {
  switch (answerLabelSelection) {
    case BIPOLAR_LABEL_OPTIONS.LEFT:
      return String(matrixAnswer.beforeLabel);
    case BIPOLAR_LABEL_OPTIONS.RIGHT:
      return String(matrixAnswer.afterLabel);
    case BIPOLAR_LABEL_OPTIONS.BOTH: {
      const result = `<div>${getInnerContentFromHtmlString(
        matrixAnswer.beforeLabel,
      )} | ${getInnerContentFromHtmlString(matrixAnswer.afterLabel)}</div>`;
      return result;
    }
    default:
      return '';
  }
};

export const getCarriedQuestionAnswerSelectionOptions = ({
  carryFromQuestionType,
  carryToQuestionType,
}) => {
  switch (carryFromQuestionType) {
    case QUESTION_SINGLE:
    case QUESTION_MULTIPLE:
      return [
        ANSWER_OPTIONS.SELECTED_VALUES,
        ANSWER_OPTIONS.UNSELECTED_VALUES,
        ANSWER_OPTIONS.ALL_VALUES,
        ANSWER_OPTIONS.SPECIFIC_SELECTED_VALUES,
        ANSWER_OPTIONS.SPECIFIC_UNSELECTED_VALUES,
        ANSWER_OPTIONS.VISIBLE_VALUES,
        ANSWER_OPTIONS.HIDDEN_VALUES,
      ];
    case QUESTION_RANKING:
      return [
        ANSWER_OPTIONS.GREATER_THAN,
        ANSWER_OPTIONS.LESSER_THAN,
        ANSWER_OPTIONS.EQUAL_TO,
        ANSWER_OPTIONS.HIGHEST_VALUE,
        ANSWER_OPTIONS.LOWEST_VALUE,
        ANSWER_OPTIONS.ALL_VALUES,
        ANSWER_OPTIONS.VISIBLE_VALUES,
        ANSWER_OPTIONS.HIDDEN_VALUES,
      ];
    case QUESTION_OPEN_ENDED:
    case QUESTION_NUMERIC_OPEN_ENDED:
    case QUESTION_SLIDER:
      return [ANSWER_OPTIONS.VALUE];
    case QUESTION_MATRIX_SINGLE:
    case QUESTION_MATRIX_MULTIPLE:
      if (
        carryToQuestionType === QUESTION_MATRIX_SINGLE ||
        carryToQuestionType === QUESTION_MATRIX_MULTIPLE
      ) {
        return [
          ANSWER_OPTIONS.ALL_COLUMNS,
          ANSWER_OPTIONS.ALL_ROWS,
          ANSWER_OPTIONS.ALL_ROWS_ALL_COLUMNS,
          ANSWER_OPTIONS.SELECTED_COLUMNS,
          ANSWER_OPTIONS.UNSELECTED_COLUMNS,
          ANSWER_OPTIONS.SPECIFIC_SELECTED_COLUMNS,
          ANSWER_OPTIONS.SPECIFIC_UNSELECTED_COLUMNS,
          ANSWER_OPTIONS.SELECTED_FOR_COLUMN,
          ANSWER_OPTIONS.UNSELECTED_FOR_COLUMN,
          ANSWER_OPTIONS.SELECTED_FOR_ROW,
          ANSWER_OPTIONS.UNSELECTED_FOR_ROW,
          ANSWER_OPTIONS.VISIBLE_ROWS,
          ANSWER_OPTIONS.HIDDEN_ROWS,
          ANSWER_OPTIONS.VISIBLE_COLUMNS,
          ANSWER_OPTIONS.HIDDEN_COLUMNS,
        ];
      }
      return [
        ANSWER_OPTIONS.ALL_COLUMNS,
        ANSWER_OPTIONS.ALL_ROWS,
        ANSWER_OPTIONS.SELECTED_COLUMNS,
        ANSWER_OPTIONS.UNSELECTED_COLUMNS,
        ANSWER_OPTIONS.SPECIFIC_SELECTED_COLUMNS,
        ANSWER_OPTIONS.SPECIFIC_UNSELECTED_COLUMNS,
        ANSWER_OPTIONS.SELECTED_FOR_COLUMN,
        ANSWER_OPTIONS.UNSELECTED_FOR_COLUMN,
        ANSWER_OPTIONS.SELECTED_FOR_ROW,
        ANSWER_OPTIONS.UNSELECTED_FOR_ROW,
        ANSWER_OPTIONS.VISIBLE_ROWS,
        ANSWER_OPTIONS.HIDDEN_ROWS,
        ANSWER_OPTIONS.VISIBLE_COLUMNS,
        ANSWER_OPTIONS.HIDDEN_COLUMNS,
      ];
    case QUESTION_MATRIX_BIPOLAR: {
      return [
        // ANSWER_OPTIONS.MATRIX_SELECTED_FOR_ROW,
        ANSWER_OPTIONS.MATRIX_EQUAL,
        ANSWER_OPTIONS.MATRIX_NOT_EQUAL,
        ANSWER_OPTIONS.MATRIX_HIGHEST_VALUE,
        ANSWER_OPTIONS.MATRIX_LOWEST_VALUE,
        ANSWER_OPTIONS.MATRIX_GREATER_THAN,
        ANSWER_OPTIONS.MATRIX_EQUAL_GREATER_THAN,
        ANSWER_OPTIONS.MATRIX_LESSER_THAN,
        ANSWER_OPTIONS.MATRIX_EQUAL_LESSER_THAN,
        ANSWER_OPTIONS.MATRIX_ALL_ROWS,
      ];
    }
    case QUESTION_SINGLE_PEEQ:
    case QUESTION_MULTIPLE_PEEQ:
      return [
        ANSWER_OPTIONS.SELECTED_VALUES,
        ANSWER_OPTIONS.UNSELECTED_VALUES,
        ANSWER_OPTIONS.ALL_VALUES,
      ];
    // questions whose values cannot be carried forward
    default:
      return [];
  }
};
export const getCarriedQuestionAnswerAsOptions = (questionType) => {
  if (isMatrixAndNotBipolarQuestionType(questionType)) {
    return [FORWARD_ANSWER_AS.ANSWERS, FORWARD_ANSWER_AS.SCOREPOINTS];
  }

  return [FORWARD_ANSWER_AS.ANSWERS];
};

export const getCarriedQuestionAnswerLabelSelectionOptions = (questionType) => {
  if (isBipolarQuestionType(questionType)) {
    return [
      BIPOLAR_LABEL_OPTIONS.BOTH,
      BIPOLAR_LABEL_OPTIONS.LEFT,
      BIPOLAR_LABEL_OPTIONS.RIGHT,
    ];
  }

  return [];
};

export const getCarriedQuestionAnswers = ({ forwardedDetails, questions }) => {
  const {
    questionRefId: sourceQuestionRefId,
    answerSelection,
    answerLabelSelection,
    answerCompareValue = [],
  } = forwardedDetails;
  const sourceQuestion = find(questions, ['refId', sourceQuestionRefId]);
  const {
    answers: answersToCarry = [],
    scorepoints: scorepointsToCarry = [],
    matrix: { matrixAnswers: matrixAnswerToCarry = [] } = {},
    type: sourceType,
  } = sourceQuestion;

  switch (sourceType) {
    case QUESTION_SINGLE:
    case QUESTION_MULTIPLE:
    case QUESTION_RANKING:
    case QUESTION_SINGLE_PEEQ:
    case QUESTION_MULTIPLE_PEEQ: {
      const forwardedAnswers = map(
        reject(answersToCarry, (answer) => isSingleMultipleAnswerEmpty(answer)),
        (answer) => omit(answer, ['questionRefId', 'originalAnswerRefId']),
      );
      switch (answerSelection) {
        case ANSWER_OPTIONS.SPECIFIC_SELECTED_VALUES:
        case ANSWER_OPTIONS.SPECIFIC_UNSELECTED_VALUES: {
          return forwardedAnswers.filter((answer) =>
            answerCompareValue.includes(answer.refId),
          );
        }
        default:
          return forwardedAnswers;
      }
    }
    case QUESTION_OPEN_ENDED:
    case QUESTION_NUMERIC_OPEN_ENDED:
    case QUESTION_SLIDER: {
      return [
        {
          body: getCarriedValuePlaceHolder(sourceType),
        },
      ];
    }
    case QUESTION_MATRIX_BIPOLAR: {
      const forwardedAnswers = reject(answersToCarry, 'isCarriedForward');
      return forwardedAnswers.map((answer) => {
        const matrixAnswer = find(matrixAnswerToCarry, [
          'answerRefId',
          answer.refId,
        ]);
        const body = getBipolarAnswerBody(matrixAnswer, answerLabelSelection);
        return {
          ...answer,
          body,
        };
      });
    }
    case QUESTION_MATRIX_SINGLE:
    case QUESTION_MATRIX_MULTIPLE: {
      switch (answerSelection) {
        case ANSWER_OPTIONS.ALL_COLUMNS:
        case ANSWER_OPTIONS.SELECTED_COLUMNS:
        case ANSWER_OPTIONS.UNSELECTED_COLUMNS:
        case ANSWER_OPTIONS.SELECTED_FOR_ROW:
        case ANSWER_OPTIONS.UNSELECTED_FOR_ROW:
        case ANSWER_OPTIONS.SPECIFIC_SELECTED_COLUMNS:
        case ANSWER_OPTIONS.SPECIFIC_UNSELECTED_COLUMNS:
        case ANSWER_OPTIONS.VISIBLE_COLUMNS:
        case ANSWER_OPTIONS.HIDDEN_COLUMNS: {
          // from columns / scorepoints
          const forwardedScorepoints = reject(
            scorepointsToCarry,
            (scorepoint) => isEmptyMatrixAnswer(scorepoint),
          );
          switch (answerSelection) {
            case ANSWER_OPTIONS.SPECIFIC_SELECTED_COLUMNS:
            case ANSWER_OPTIONS.SPECIFIC_UNSELECTED_COLUMNS: {
              return forwardedScorepoints.filter((scorepoint) =>
                answerCompareValue.includes(scorepoint.refId),
              );
            }
            default:
              return forwardedScorepoints;
          }
        }
        case ANSWER_OPTIONS.ALL_ROWS:
        case ANSWER_OPTIONS.SELECTED_FOR_COLUMN:
        case ANSWER_OPTIONS.UNSELECTED_FOR_COLUMN:
        case ANSWER_OPTIONS.VISIBLE_ROWS:
        case ANSWER_OPTIONS.HIDDEN_ROWS: {
          // from rows / answers
          return reject(answersToCarry, (answer) =>
            isEmptyMatrixAnswer(answer),
          );
        }
        case ANSWER_OPTIONS.ALL_ROWS_ALL_COLUMNS: {
          return { answersToCarry, scorepointsToCarry };
        }
        default:
          return [];
      }
    }
    default:
      return null;
  }
};

export const getAnswerCompareValues = ({
  sourceQuestion = {},
  answerSelection,
}) => {
  const { type: questionType } = sourceQuestion;
  switch (questionType) {
    case QUESTION_SINGLE:
    case QUESTION_MULTIPLE: {
      if (
        [
          ANSWER_OPTIONS.SPECIFIC_SELECTED_VALUES,
          ANSWER_OPTIONS.SPECIFIC_UNSELECTED_VALUES,
        ].includes(answerSelection)
      ) {
        return sourceQuestion.answers;
      }
      return null;
    }
    case QUESTION_MATRIX_SINGLE:
    case QUESTION_MATRIX_MULTIPLE: {
      if (
        [
          ANSWER_OPTIONS.SELECTED_FOR_COLUMN,
          ANSWER_OPTIONS.UNSELECTED_FOR_COLUMN,
          ANSWER_OPTIONS.SPECIFIC_SELECTED_COLUMNS,
          ANSWER_OPTIONS.SPECIFIC_UNSELECTED_COLUMNS,
        ].includes(answerSelection)
      ) {
        return sourceQuestion.scorepoints;
      }
      if (
        [
          ANSWER_OPTIONS.SELECTED_FOR_ROW,
          ANSWER_OPTIONS.UNSELECTED_FOR_ROW,
        ].includes(answerSelection)
      ) {
        return sourceQuestion.answers;
      }
      return null;
    }
    case QUESTION_RANKING: {
      const rankingAnswers = get(sourceQuestion, 'answers', []);
      if (
        answerSelection === ANSWER_OPTIONS.HIGHEST_VALUE ||
        answerSelection === ANSWER_OPTIONS.LOWEST_VALUE ||
        answerSelection === ANSWER_OPTIONS.ALL_VALUES ||
        answerSelection === ANSWER_OPTIONS.VISIBLE_VALUES ||
        answerSelection === ANSWER_OPTIONS.HIDDEN_VALUES
      ) {
        return null;
      }
      if (answerSelection === ANSWER_OPTIONS.GREATER_THAN) {
        return range(1, rankingAnswers.length - 1);
      }
      if (answerSelection === ANSWER_OPTIONS.LESSER_THAN) {
        return range(3, rankingAnswers.length + 1);
      }
      return range(1, rankingAnswers.length + 1);
    }
    case QUESTION_MATRIX_BIPOLAR: {
      const columns = get(sourceQuestion, 'matrix.columns', []);
      if (
        answerSelection === ANSWER_OPTIONS.MATRIX_HIGHEST_VALUE ||
        answerSelection === ANSWER_OPTIONS.MATRIX_LOWEST_VALUE
      ) {
        return null;
      }
      if (answerSelection === ANSWER_OPTIONS.MATRIX_GREATER_THAN) {
        return range(1, columns.length - 1);
      }
      if (answerSelection === ANSWER_OPTIONS.MATRIX_EQUAL_GREATER_THAN) {
        return range(1, columns.length);
      }
      if (answerSelection === ANSWER_OPTIONS.MATRIX_LESSER_THAN) {
        return range(3, columns.length + 1);
      }
      if (answerSelection === ANSWER_OPTIONS.MATRIX_EQUAL_LESSER_THAN) {
        return range(2, columns.length + 1);
      }
      return range(1, columns.length + 1);
    }
    default:
      return null;
  }
};

export const applyCarriedQuestionAnswers = ({
  previousCarryForward,
  forwardedAnswers,
  question,
  forwardedDetails,
  questions,
}) => {
  const {
    answerAs,
    questionRefId: currentQuestionRefId,
    answerSelection,
    answerLabelSelection,
    refId: configurationRefId,
  } = forwardedDetails;

  const previousSetting = find(previousCarryForward, [
    'refId',
    configurationRefId,
  ]);
  const { questionRefId: previousQuestionRefId } = previousSetting;

  const sourceQuestion = find(questions, ['refId', currentQuestionRefId]) || {};
  const {
    type: sourceType,
    matrix: { matrixAnswers: sourceMatrixAnswers = [] } = {},
  } = sourceQuestion;

  switch (question.type) {
    case QUESTION_SINGLE:
    case QUESTION_MULTIPLE:
    case QUESTION_RANKING: {
      const { answers: targetAnswers } = question;
      let targetAnswersToKeep = reject(targetAnswers, 'isEmptyAnswer');
      const isSameSourceQuestion =
        currentQuestionRefId === previousQuestionRefId;
      if (!isSameSourceQuestion) {
        targetAnswersToKeep = reject(targetAnswersToKeep, [
          'questionRefId',
          previousQuestionRefId,
        ]);
      }

      const { updatedAnswers } = upsertDeleteTargetFromSource({
        fromItems: forwardedAnswers,
        fromMatrixAnswers: sourceMatrixAnswers,
        toItems: targetAnswersToKeep,
        sourceRefId: currentQuestionRefId,
        sourceType,
        answerSelection,
        answerLabelSelection,
        targetType: question.type,
      });
      if (updatedAnswers === targetAnswersToKeep && isSameSourceQuestion) {
        return { answers: targetAnswers };
      }
      const answerUpdates = [...updatedAnswers, getEmptySingleMultipleAnswer()];
      return { answers: answerUpdates };
    }
    case QUESTION_MATRIX_SINGLE:
    case QUESTION_MATRIX_MULTIPLE: {
      const { answers: targetAnswers, scorepoints: targetScorepoints } =
        question;

      if (!Array.isArray(forwardedAnswers)) {
        const { updatedAnswers } = upsertDeleteTargetFromSource({
          fromItems: forwardedAnswers.answersToCarry,
          toItems: targetAnswers,
          sourceRefId: currentQuestionRefId,
          sourceType,
          answerSelection,
          answerLabelSelection,
          targetType: question.type,
          isScorepoint: false,
        });

        const { updatedAnswers: updatedScorepoints } =
          upsertDeleteTargetFromSource({
            fromItems: forwardedAnswers.scorepointsToCarry,
            toItems: targetScorepoints,
            sourceRefId: currentQuestionRefId,
            sourceType,
            answerSelection,
            answerLabelSelection,
            targetType: question.type,
            isScorepoint: true,
          });

        return {
          answers: updatedAnswers,
          scorepoints: updatedScorepoints,
        };
      }

      if (answerAs === FORWARD_ANSWER_AS.ANSWERS) {
        let targetAnswersToKeep = targetAnswers;

        if (currentQuestionRefId !== previousQuestionRefId) {
          targetAnswersToKeep = reject(targetAnswersToKeep, [
            'questionRefId',
            previousQuestionRefId,
          ]);
        }

        const { updatedAnswers } = upsertDeleteTargetFromSource({
          fromItems: forwardedAnswers,
          toItems: targetAnswersToKeep,
          sourceRefId: currentQuestionRefId,
          sourceType,
          answerSelection,
          answerLabelSelection,
          targetType: question.type,
        });
        const updatedScorepoints = reject(targetScorepoints, [
          'questionRefId',
          previousQuestionRefId,
        ]);

        return { answers: updatedAnswers, scorepoints: updatedScorepoints };
      }
      if (answerAs === FORWARD_ANSWER_AS.SCOREPOINTS) {
        let targetScorepointsToKeep = targetScorepoints;

        if (currentQuestionRefId !== previousQuestionRefId) {
          targetScorepointsToKeep = reject(targetScorepointsToKeep, [
            'questionRefId',
            previousQuestionRefId,
          ]);
        }

        const { updatedAnswers: updatedScorepoints } =
          upsertDeleteTargetFromSource({
            fromItems: forwardedAnswers,
            toItems: targetScorepointsToKeep,
            sourceRefId: currentQuestionRefId,
            sourceType,
            answerSelection,
            answerLabelSelection,
            targetType: question.type,
          });
        const updatedAnswers = reject(targetAnswers, [
          'questionRefId',
          previousQuestionRefId,
        ]);

        return { answers: updatedAnswers, scorepoints: updatedScorepoints };
      }
      return { answers: targetAnswers, scorepoints: targetScorepoints };
    }
    default:
      return { answers: question.answers, scorepoints: question.scorepoints };
  }
};

const updateTargetFromSource = ({
  sourceType,
  sourceRefId,
  toItems,
  fromItems,
  fromMatrixAnswers,
  answerLabelSelection,
  targetType,
  sourceIsBipolar,
  isChangingNewMatrixProperties,
}) => {
  const updatedRefIds = [];
  const updatedItems = toItems.map((item) => {
    const originalRefId =
      item.originalAnswerRefId || item.originalScorepointRefId;
    const updatedData = find(fromItems, ['refId', originalRefId]);

    if (updatedData) {
      updatedRefIds.push(originalRefId);
      if (sourceIsBipolar) {
        const matrixAnswerBody = getBipolarAnswerBodyByRefId(
          fromMatrixAnswers,
          updatedData.refId,
          answerLabelSelection,
        );

        return updateCarriedAnswer(
          item,
          { ...updatedData, body: matrixAnswerBody },
          targetType,
        );
      }
      // we do not use the targetType here because the new matrix schema
      // also affects "answers" apart from "matrixAnswers" and "columns"
      // therefore we don't want to also add "beforeLabel" props to the "answers" array.
      if (isChangingNewMatrixProperties) {
        return updateCarriedMatrixAnswer(item, updatedData, targetType);
      }
      return updateCarriedAnswer(item, updatedData, targetType);
    }
    if (
      item.isCarriedForward &&
      item.questionRefId === sourceRefId &&
      [
        QUESTION_OPEN_ENDED,
        QUESTION_NUMERIC_OPEN_ENDED,
        QUESTION_SLIDER,
      ].includes(sourceType)
    ) {
      // originalRefId is undefined, but we need it here to signify that an
      // update has occurred (used later)
      updatedRefIds.push(originalRefId);
      const newAnswerPayload = {
        body: getCarriedValuePlaceHolder(sourceType),
      };
      if (isChangingNewMatrixProperties) {
        return updateCarriedMatrixAnswer(item, newAnswerPayload, targetType);
      }
      return updateCarriedAnswer(item, newAnswerPayload, targetType);
    }
    return item;
  });

  return {
    updatedItems,
    updatedRefIds,
  };
};

const createTargetFromSource = ({
  sourceRefId,
  fromItems,
  fromMatrixAnswers,
  answerLabelSelection,
  targetType,
  changedProperty,
  sourceIsBipolar,
  originalIsScorepoint,
  shouldCreateAnswer,
  shouldCreateMatrixAnswer,
}) => {
  const newItems = [];
  const newItemsMatrix = [];
  fromItems.forEach((newItem) => {
    if (shouldCreateAnswer && sourceIsBipolar) {
      const matrixAnswerBody = getBipolarAnswerBodyByRefId(
        fromMatrixAnswers,
        newItem.refId,
        answerLabelSelection,
      );
      newItems.push(
        createCarriedAnswer({
          questionRefId: sourceRefId,
          answer: { ...newItem, body: matrixAnswerBody },
          isFromScorepoint: originalIsScorepoint,
          targetType,
        }),
      );
      return;
    }

    const newAnswer = createCarriedAnswer({
      questionRefId: sourceRefId,
      answer: newItem,
      isFromScorepoint: originalIsScorepoint,
      targetType,
    });
    newItems.push(newAnswer);

    if (shouldCreateMatrixAnswer) {
      const newItemMatrix = createCarriedMatrixAnswer({
        questionRefId: sourceRefId,
        answer: newItem,
        isFromScorepoint: originalIsScorepoint,
        targetType,
        changedProperty,
      });
      if (newAnswer && changedProperty === MATRIX_ANSWER_PROP) {
        newItemMatrix.answerRefId = newAnswer.refId;
      }
      newItemsMatrix.push(newItemMatrix);
    }
  });

  return { newItems, newItemsMatrix };
};

const cleanTargetFromSource = ({
  sourceRefId,
  fromItems,
  toItems,
  updatedRefIds,
  updatedItems,
  newItems,
  sourceIsOpenEnded,
}) => {
  let hasChanges = Boolean(updatedRefIds.length || newItems.length);
  const finalItems = reject(
    [...updatedItems, ...newItems],
    ({ questionRefId, originalAnswerRefId, originalScorepointRefId }) => {
      const shouldDelete =
        !sourceIsOpenEnded &&
        questionRefId === sourceRefId &&
        !find(
          fromItems,
          (item) =>
            originalAnswerRefId === item.refId ||
            originalScorepointRefId === item.refId,
        );
      if (shouldDelete) {
        hasChanges = true;
      }
      return shouldDelete;
    },
  );

  if (hasChanges) {
    return finalItems;
  }
  return toItems;
};

const upsertDeleteTargetFromSource = ({
  fromItems = [],
  fromMatrixAnswers = [],
  toItems = [],
  toItemsMatrixAnswers = [],
  sourceRefId,
  sourceType,
  answerSelection,
  answerLabelSelection,
  targetType,
  changedProperty,
  isScorepoint = false, // this exists for cases where you wouldn't get this info based only on the ANSWER_OPTION
}) => {
  const sourceIsOpenEnded =
    isOpenEndedOrNumericOpenEndedOrSliderQuestionType(sourceType);

  const sourceIsMatrix = isMatrixSingleMultipleQuestionType(sourceType);
  const sourceIsBipolar = isBipolarQuestionType(sourceType);

  const originalIsScorepoint =
    (sourceIsMatrix &&
      ![
        ANSWER_OPTIONS.SELECTED_FOR_COLUMN,
        ANSWER_OPTIONS.UNSELECTED_FOR_COLUMN,
        ANSWER_OPTIONS.ALL_ROWS,
        ANSWER_OPTIONS.ALL_ROWS_ALL_COLUMNS,
        ANSWER_OPTIONS.VISIBLE_ROWS,
        ANSWER_OPTIONS.HIDDEN_ROWS,
      ].includes(answerSelection)) ||
    isScorepoint;

  const { updatedItems, updatedRefIds } = updateTargetFromSource({
    sourceType,
    sourceRefId,
    toItems,
    fromItems,
    fromMatrixAnswers,
    answerLabelSelection,
    targetType,
    sourceIsBipolar,
    isChangingNewMatrixProperties: false,
  });

  const {
    updatedItems: updatedItemsMatrix,
    updatedRefIds: updatedRefIdsMatrix,
  } = updateTargetFromSource({
    sourceType,
    sourceRefId,
    toItems: toItemsMatrixAnswers,
    fromItems,
    fromMatrixAnswers,
    answerLabelSelection,
    targetType,
    sourceIsBipolar,
    isChangingNewMatrixProperties: isNewMatrixProp(changedProperty),
  });

  const updatedCombinedRefIds = uniqBy(
    [...updatedRefIds, ...updatedRefIdsMatrix],
    (refId) => refId,
  );

  const { newItems, newItemsMatrix } = createTargetFromSource({
    fromItems: reject(fromItems, ({ refId }) =>
      updatedCombinedRefIds.includes(refId),
    ),
    sourceRefId,
    fromMatrixAnswers,
    answerLabelSelection,
    targetType,
    changedProperty,
    sourceIsBipolar,
    originalIsScorepoint,
    shouldCreateAnswer: Boolean(toItems.length),
    shouldCreateMatrixAnswer: Boolean(toItemsMatrixAnswers.length),
  });

  const updatedAnswers = cleanTargetFromSource({
    sourceRefId,
    fromItems,
    toItems,
    updatedRefIds,
    updatedItems,
    newItems,
    sourceIsOpenEnded,
  });

  const updatedMatrixAnswers = cleanTargetFromSource({
    sourceRefId,
    fromItems,
    toItems: toItemsMatrixAnswers,
    updatedRefIds: updatedRefIdsMatrix,
    updatedItems: updatedItemsMatrix,
    newItems: newItemsMatrix,
    sourceIsOpenEnded,
  });

  return { updatedAnswers, updatedMatrixAnswers };
};

const updateCarriedValues = ({
  question,
  sourceQuestion,
  carryForwardConfig,
}) => {
  const {
    answers: targetAnswers,
    scorepoints: targetScorepoints,
    matrix: targetMatrix,
    type: targetType,
  } = question;
  const {
    answers: sourceAnswers,
    scorepoints: sourceScorepoints,
    matrix: { matrixAnswers: sourceMatrixAnswers } = {},
    refId: sourceRefId,
    type: sourceType,
  } = sourceQuestion;
  const {
    answerSelection,
    answerAs,
    answerLabelSelection,
    answerCompareValue = [],
  } = carryForwardConfig;

  const cleanedConfig = { answerSelection, answerCompareValue };
  const cleanedSourceAnswers = getSourceCleanedCarryForward({
    ...cleanedConfig,
    answers: sourceAnswers,
  });
  const cleanedSourceScorepoints = getSourceCleanedCarryForward({
    ...cleanedConfig,
    answers: sourceScorepoints,
  });
  const cleanedSourceMatrixAnswers = getSourceCleanedCarryForward({
    ...cleanedConfig,
    answers: sourceMatrixAnswers,
  });

  let answers = targetAnswers;
  let scorepoints = targetScorepoints;
  let matrixAnswers = targetMatrix?.matrixAnswers;
  let matrixColumns = targetMatrix?.columns;
  const sourceIsMatrix = [
    QUESTION_MATRIX_SINGLE,
    QUESTION_MATRIX_MULTIPLE,
  ].includes(sourceType);

  const targetIsNewMatrix = [
    QUESTION_MATRIX_OPEN_ENDED,
    QUESTION_MATRIX_NUMERIC_OPEN_ENDED,
    QUESTION_MATRIX_CONSTANT_SUM,
  ].includes(targetType);

  if (targetIsNewMatrix) {
    if (answerAs === FORWARD_ANSWER_AS.ANSWERS) {
      ({ updatedAnswers: answers, updatedMatrixAnswers: matrixAnswers } =
        upsertDeleteTargetFromSource({
          fromItems: cleanedSourceAnswers,
          toItems: targetAnswers,
          toItemsMatrixAnswers: targetMatrix.matrixAnswers,
          sourceRefId,
          sourceType,
          answerSelection,
          answerLabelSelection,
          targetType: question.type,
          changedProperty: MATRIX_ANSWER_PROP,
        }));

      answers = answers.map((answer) => ({ ...answer, body: '' }));
    } else if (answerAs === FORWARD_ANSWER_AS.SCOREPOINTS) {
      ({ updatedMatrixAnswers: matrixColumns } = upsertDeleteTargetFromSource({
        fromItems: cleanedSourceAnswers,
        toItemsMatrixAnswers: targetMatrix.columns,
        sourceRefId,
        sourceType,
        answerSelection,
        answerLabelSelection,
        targetType: question.type,
        matrixAnswers,
        changedProperty: MATRIX_COLUMN_PROP,
      }));
    }

    return { answers, matrixAnswers, matrixColumns };
  }

  if (answerAs === FORWARD_ANSWER_AS.ANSWERS) {
    if (
      sourceIsMatrix &&
      ![
        ANSWER_OPTIONS.ALL_ROWS,
        ANSWER_OPTIONS.SELECTED_FOR_COLUMN,
        ANSWER_OPTIONS.UNSELECTED_FOR_COLUMN,
        ANSWER_OPTIONS.VISIBLE_ROWS,
        ANSWER_OPTIONS.HIDDEN_ROWS,
      ].includes(answerSelection)
    ) {
      ({ updatedAnswers: answers } = upsertDeleteTargetFromSource({
        fromItems: cleanedSourceScorepoints,
        fromMatrixAnswers: cleanedSourceMatrixAnswers,
        toItems: targetAnswers,
        sourceRefId,
        sourceType,
        answerSelection,
        answerLabelSelection,
        targetType,
      }));
    } else {
      ({ updatedAnswers: answers } = upsertDeleteTargetFromSource({
        fromMatrixAnswers: cleanedSourceMatrixAnswers,
        fromItems: cleanedSourceAnswers,
        toItems: targetAnswers,
        sourceRefId,
        sourceType,
        answerSelection,
        answerLabelSelection,
        targetType,
      }));
    }
  } else if (answerAs === FORWARD_ANSWER_AS.SCOREPOINTS) {
    ({ updatedAnswers: scorepoints } = upsertDeleteTargetFromSource({
      fromItems: cleanedSourceAnswers,
      toItems: targetScorepoints,
      sourceRefId,
      sourceType,
      answerSelection,
      answerLabelSelection,
      targetType,
    }));
  }

  return { answers, scorepoints };
};

export function getAffectedCarryForwardQuestions({
  questions,
  sourceQuestion,
}) {
  const affectedSet = new Set();
  const affectedQuestions = [];
  const queue = [sourceQuestion];

  while (queue.length > 0) {
    const currentSourceQuestion = queue.shift();

    questions.forEach((question) => {
      const { carryForward = [], refId } = question;
      if (
        carryForward.some(
          ({ questionRefId }) => questionRefId === currentSourceQuestion.refId,
        )
      ) {
        if (!affectedSet.has(refId)) {
          affectedSet.add(refId);
          affectedQuestions.push({
            question,
            sourceQuestion: currentSourceQuestion,
          });
          queue.push(question);
        }
      }
    });
  }

  return affectedQuestions;
}

export function getAffectedLoopAndMergeGroups({ questions, sourceQuestion }) {
  return questions.filter((question) => {
    const { type, loopAndMergeFromQuestionConfiguration } = question;
    if (type !== GROUP_LOOP_MERGE || !loopAndMergeFromQuestionConfiguration) {
      return false;
    }

    return (
      loopAndMergeFromQuestionConfiguration.questionRefId ===
      sourceQuestion.refId
    );
  });
}

export const updateRelevantCarriedQuestions = ({
  questions,
  sourceQuestion,
  mustRemoveRelevantCarryForwardSetting = false,
}) => {
  const affectedQuestions = getAffectedCarryForwardQuestions({
    questions,
    sourceQuestion,
  });

  if (!affectedQuestions.length) return questions;

  return questions.reduce((acc, currentQuestion) => {
    const affected = affectedQuestions.find(
      (affectedQuestion) =>
        affectedQuestion.question.refId === currentQuestion.refId,
    );

    if (!affected) {
      acc.push(currentQuestion);
      return acc;
    }

    // Use previously updated version of sourceQuestion if available
    const previouslyUpdatedSourceQuestion =
      acc.find((q) => q.refId === affected.sourceQuestion.refId) ||
      affected.sourceQuestion;

    const updatedQuestion = { ...affected.question };
    const carryForwardConfig = affected.question.carryForward.find(
      ({ questionRefId }) =>
        questionRefId === previouslyUpdatedSourceQuestion.refId,
    );

    const canCarryForward =
      !mustRemoveRelevantCarryForwardSetting &&
      canCarryForwardFrom({
        targetType: affected.question.type,
        sourceType: previouslyUpdatedSourceQuestion.type,
      });

    let {
      answers: updatedAnswers,
      scorepoints: updatedScorepoints,
      matrixAnswers: updatedMatrixAnswers,
      matrixColumns: updatedMatrixColumns,
    } = updateCarriedValues({
      question: affected.question,
      sourceQuestion: previouslyUpdatedSourceQuestion,
      carryForwardConfig,
    });

    if (!canCarryForward) {
      updatedAnswers = reject(updatedAnswers, [
        'questionRefId',
        previouslyUpdatedSourceQuestion.refId,
      ]);
      updatedScorepoints = reject(updatedScorepoints, [
        'questionRefId',
        previouslyUpdatedSourceQuestion.refId,
      ]);
      updatedMatrixAnswers = reject(updatedMatrixAnswers, [
        'questionRefId',
        previouslyUpdatedSourceQuestion.refId,
      ]);
      updatedMatrixColumns = reject(updatedMatrixColumns, [
        'questionRefId',
        previouslyUpdatedSourceQuestion.refId,
      ]);

      Object.assign(updatedQuestion, {
        carryForward: reject(
          affected.question.carryForward,
          carryForwardConfig,
        ),
      });
    } else {
      const {
        refId: carryForwardConfigId,
        answerSelection,
        answerCompareValue = [],
      } = carryForwardConfig;

      const isMissingSelectedScorepoint =
        [
          ANSWER_OPTIONS.SELECTED_FOR_COLUMN,
          ANSWER_OPTIONS.UNSELECTED_FOR_COLUMN,
          ANSWER_OPTIONS.SPECIFIC_SELECTED_COLUMNS,
          ANSWER_OPTIONS.SPECIFIC_UNSELECTED_COLUMNS,
        ].includes(answerSelection) &&
        some(
          answerCompareValue,
          (value) =>
            !find(previouslyUpdatedSourceQuestion.scorepoints, [
              'refId',
              value,
            ]),
        );

      const isMissingSelectedAnswer =
        [
          ANSWER_OPTIONS.SELECTED_FOR_ROW,
          ANSWER_OPTIONS.UNSELECTED_FOR_ROW,
          ANSWER_OPTIONS.SPECIFIC_SELECTED_VALUES,
          ANSWER_OPTIONS.SPECIFIC_UNSELECTED_VALUES,
        ].includes(answerSelection) &&
        some(
          answerCompareValue,
          (value) =>
            !find(previouslyUpdatedSourceQuestion.answers, ['refId', value]),
        );

      const isOutOfBounds =
        isRankingQuestionType(previouslyUpdatedSourceQuestion.type) &&
        ((answerSelection === ANSWER_OPTIONS.EQUAL_TO &&
          answerCompareValue >
            previouslyUpdatedSourceQuestion.answers.length) ||
          (answerSelection === ANSWER_OPTIONS.GREATER_THAN &&
            answerCompareValue >
              previouslyUpdatedSourceQuestion.answers.length - 2) ||
          (answerSelection === ANSWER_OPTIONS.LESSER_THAN &&
            answerCompareValue >
              previouslyUpdatedSourceQuestion.answers.length));

      const getUpdatedCarryForward = (newAnswerCompareValue) =>
        affected.question.carryForward.map((carryForwardItem) =>
          carryForwardItem.refId !== carryForwardConfigId
            ? carryForwardItem
            : {
                ...carryForwardConfig,
                answerCompareValue: newAnswerCompareValue,
              },
        );

      if (isMissingSelectedScorepoint) {
        Object.assign(updatedQuestion, {
          carryForward: getUpdatedCarryForward(
            reject(
              answerCompareValue,
              (value) =>
                !find(previouslyUpdatedSourceQuestion.scorepoints, [
                  'refId',
                  value,
                ]),
            ),
          ),
        });
      } else if (isMissingSelectedAnswer) {
        Object.assign(updatedQuestion, {
          carryForward: getUpdatedCarryForward(
            reject(
              answerCompareValue,
              (value) =>
                !find(previouslyUpdatedSourceQuestion.answers, [
                  'refId',
                  value,
                ]),
            ),
          ),
        });
      } else if (isOutOfBounds) {
        Object.assign(updatedQuestion, {
          carryForward: getUpdatedCarryForward([]),
        });
      }
    }

    Object.assign(updatedQuestion, { answers: updatedAnswers });

    if (isMatrixSingleMultipleQuestionType(updatedQuestion.type)) {
      Object.assign(updatedQuestion, { scorepoints: updatedScorepoints });
    }

    if (isMatrixOpenEndedType(updatedQuestion.type)) {
      Object.assign(updatedQuestion, {
        matrix: {
          ...updatedQuestion.matrix,
          matrixAnswers: updatedMatrixAnswers,
          columns: updatedMatrixColumns,
          answerColumnValueTypes: getAnswerValueColumnTypesOpenEnded({
            answers: updatedAnswers,
            columns: updatedMatrixColumns,
            answerColumnValueTypes:
              updatedQuestion.matrix.answerColumnValueTypes,
          }),
        },
      });
    }

    acc.push(updatedQuestion);
    return acc;
  }, []);
};

export function getConceptBySourceId(loopAndMergeNode, sourceId) {
  const mergedAttribute = find(loopAndMergeNode.attributes, [
    'type',
    LOOP_AND_MERGE_ATTRIBUTE_TYPE,
  ]);
  const conceptIndex = findIndex(
    mergedAttribute?.values,
    (value) =>
      value.originalAnswerRefId === sourceId ||
      value.originalScorepointRefId === sourceId,
  );
  const concept = loopAndMergeNode.concepts[conceptIndex];
  return concept;
}

export const updateRelevantLoopAndMergeGroups = ({
  questions,
  sourceQuestion,
  mustRemoveRelevantLoopAndMergeSetting = false,
}) => {
  const affectedQuestions = getAffectedLoopAndMergeGroups({
    questions,
    sourceQuestion,
  });

  if (!affectedQuestions.length) return questions;

  return questions.map((currentQuestion) => {
    if (!affectedQuestions.includes(currentQuestion)) {
      return currentQuestion;
    }
    return updateLoopAndMergeGroup({
      previousConfiguration:
        currentQuestion.loopAndMergeFromQuestionConfiguration,
      group: currentQuestion,
      sourceQuestion,
      mustRemoveRelevantLoopAndMergeSetting,
      questions,
    });
  }, []);
};

export const getMaxLoopAndMergeRepetitions = (
  loopAndMergeGroup,
  allQuestions,
) => {
  const { loopAndMergeFromQuestionConfiguration = {}, concepts = [] } =
    loopAndMergeGroup;
  if (isEmpty(loopAndMergeFromQuestionConfiguration)) {
    return concepts.length;
  }
  const sourceQuestion = find(allQuestions, [
    'refId',
    loopAndMergeFromQuestionConfiguration.questionRefId,
  ]);
  if (!sourceQuestion) {
    return concepts.length;
  }
  const { answerSelection, answerCompareValue = [] } =
    loopAndMergeFromQuestionConfiguration;

  const { max } = getCarriedForwardCount(
    sourceQuestion,
    answerSelection,
    answerCompareValue,
  );

  return max;
};

export const updateLoopAndMergeGroup = ({
  previousConfiguration,
  group,
  sourceQuestion,
  mustRemoveRelevantLoopAndMergeSetting,
  differentQuestionUsed = false,
}) => {
  const { loopAndMergeFromQuestionConfiguration } = group;
  let updatedGroup = cloneDeep(group);

  const canLoopAndMergeBasedOnQuestion =
    !mustRemoveRelevantLoopAndMergeSetting &&
    canLoopAndMergeFrom(sourceQuestion.type);

  if (!canLoopAndMergeBasedOnQuestion) {
    // update the questions that recall from that variable (and remove the mention)
    console.warn(
      'TODO: Update questions of the L&M group that recall from the variable',
    );

    updatedGroup = removeLoopAndMergeBasedOnQuestionConfiguration(updatedGroup);
  } else {
    console.warn('TODO: Update the L&M attribute values (add/update/delete)');
    // Add ✅
    // Update ✅
    // Delete ✅
    const {
      // refId: loopAndMergeConfigId,
      answerSelection,
      answerCompareValue = [],
    } = loopAndMergeFromQuestionConfiguration;

    const isMissingSelectedScorepoint =
      [
        ANSWER_OPTIONS.SELECTED_FOR_COLUMN,
        ANSWER_OPTIONS.UNSELECTED_FOR_COLUMN,
        ANSWER_OPTIONS.SPECIFIC_SELECTED_COLUMNS,
        ANSWER_OPTIONS.SPECIFIC_UNSELECTED_COLUMNS,
      ].includes(answerSelection) &&
      some(
        answerCompareValue,
        (value) => !find(sourceQuestion.scorepoints, ['refId', value]),
      );
    const isMissingSelectedAnswer =
      [
        ANSWER_OPTIONS.SELECTED_FOR_ROW,
        ANSWER_OPTIONS.UNSELECTED_FOR_ROW,
        ANSWER_OPTIONS.SPECIFIC_SELECTED_VALUES,
        ANSWER_OPTIONS.SPECIFIC_UNSELECTED_VALUES,
      ].includes(answerSelection) &&
      some(
        answerCompareValue,
        (value) => !find(sourceQuestion.answers, ['refId', value]),
      );

    if (isMissingSelectedScorepoint) {
      Object.assign(updatedGroup, {
        loopAndMergeFromQuestionConfiguration: {
          ...loopAndMergeFromQuestionConfiguration,
          answerCompareValue: reject(
            answerCompareValue,
            (value) => !find(sourceQuestion.scorepoints, ['refId', value]),
          ),
        },
      });
    } else if (isMissingSelectedAnswer) {
      Object.assign(updatedGroup, {
        loopAndMergeFromQuestionConfiguration: {
          ...loopAndMergeFromQuestionConfiguration,
          answerCompareValue: reject(
            answerCompareValue,
            (value) => !find(sourceQuestion.answers, ['refId', value]),
          ),
        },
      });
    }

    const previousMergeQuestionAttribute = updatedGroup.attributes.find(
      isLoopAndMergeAttributeBasedOnQuestion,
    );

    let previousValues = previousMergeQuestionAttribute?.values || [];
    if (differentQuestionUsed) {
      previousValues = [];
    }

    const relevantAnswers = getCarriedQuestionAnswers({
      forwardedDetails: loopAndMergeFromQuestionConfiguration,
      questions: [sourceQuestion],
    });

    const { answers: appliedAnswers } = applyCarriedQuestionAnswers({
      previousCarryForward: [
        previousConfiguration || loopAndMergeFromQuestionConfiguration,
      ],
      forwardedAnswers: relevantAnswers,
      question: {
        type: QUESTION_SINGLE,
        answers: previousValues,
        scorepoints: [],
        refId: group.refId,
      },
      forwardedDetails: loopAndMergeFromQuestionConfiguration,
      questions: [sourceQuestion],
    });
    const updatedValues = appliedAnswers
      .filter((answer) => !answer.isEmptyAnswer)
      .map(
        ({
          body,
          isCarriedForward,
          originalAnswerRefId,
          originalScorepointRefId,
          questionRefId,
          refId,
          id,
        }) => ({
          text: body,
          isCarriedForward,
          originalAnswerRefId,
          originalScorepointRefId,
          questionRefId,
          refId,
          id,
        }),
      );

    if (!previousMergeQuestionAttribute) {
      console.warn(
        'No previous loop and merge attribute found, creating a new one',
      );
      updatedGroup.attributes = [
        getLoopAndMergeFromQuestionAttribute(updatedValues),
        ...updatedGroup.attributes,
      ];
    }

    const removedValues = reject(previousValues, ({ refId: oldRefId }) =>
      updatedValues.find(({ refId: newRefId }) => newRefId === oldRefId),
    );

    // remove attributes values that correspond to removed values based on their index
    const removedValueIndices = removedValues.map(({ refId }) =>
      previousValues.findIndex(({ refId: oldRefId }) => oldRefId === refId),
    );
    updatedGroup.attributes = updatedGroup.attributes.map((attribute) => {
      if (isLoopAndMergeAttributeBasedOnQuestion(attribute)) {
        return { ...attribute, values: updatedValues };
      }
      const updatedAttributeValues = attribute.values
        .filter((_, index) => !removedValueIndices.includes(index))
        .slice(0, updatedValues.length);

      // ensure that the attribute values array has the same length as updatedValues
      if (updatedAttributeValues.length < updatedValues.length) {
        const newValues = new Array(
          updatedValues.length - updatedAttributeValues.length,
        )
          .fill(null)
          .map(getEmptyAttributeValue);
        return {
          ...attribute,
          values: updatedAttributeValues.concat(newValues),
        };
      }

      return {
        ...attribute,
        values: updatedAttributeValues,
      };
    });

    updatedGroup.concepts = updatedGroup.concepts
      .filter((_, index) => !removedValueIndices.includes(index))
      .slice(0, updatedValues.length)
      .map((concept, index) => ({
        ...concept,
        name: getLoopAndMergeConceptName(index),
      }));

    // ensure that the concepts array has the same length as updatedValues
    if (updatedGroup.concepts.length < updatedValues.length) {
      const newConcepts = new Array(
        updatedValues.length - updatedGroup.concepts.length,
      ).fill(null);
      updatedGroup.concepts = [
        ...updatedGroup.concepts,
        ...newConcepts.map((_, index) =>
          getEmptyConcept(
            getLoopAndMergeConceptName(updatedGroup.concepts.length + index),
          ),
        ),
      ];
    }

    if (!updatedGroup?.properties?.userDefinedRepetitions) {
      const maxRepetitions = getMaxLoopAndMergeRepetitions(updatedGroup, [
        sourceQuestion,
      ]);

      Object.assign(updatedGroup, {
        properties: {
          ...updatedGroup.properties,
          repetitions: maxRepetitions,
        },
      });
    }
  }

  return updatedGroup;
};

function getAffectedQuestionIds({ questions, sourceQuestionId }) {
  const affectedSet = new Set();
  const affectedQuestions = [];
  const queue = [sourceQuestionId];

  while (queue.length > 0) {
    const currentSourceQuestionId = queue.shift();

    questions.forEach((question) => {
      const { carryForward = [], refId } = question;
      if (
        carryForward.some(
          ({ questionRefId }) => questionRefId === currentSourceQuestionId,
        )
      ) {
        if (!affectedSet.has(refId)) {
          affectedSet.add(refId);
          affectedQuestions.push({
            question,
            currentSourceQuestionId,
          });
          queue.push(question.refId);
        }
      }
    });
  }

  return affectedQuestions;
}

export const clearRelevantCarriedQuestions = ({
  questions = [],
  deletedQuestionRefId,
}) => {
  const affectedQuestions = getAffectedQuestionIds({
    questions,
    sourceQuestionId: deletedQuestionRefId,
  });

  if (!affectedQuestions.length) return questions;

  return questions.reduce((acc, currentQuestion) => {
    const affected = affectedQuestions.find(
      (affectedQuestion) =>
        affectedQuestion.question.refId === currentQuestion.refId,
    );

    if (!affected) {
      acc.push(currentQuestion);
      return acc;
    }

    const updatedCarryForward = reject(currentQuestion.carryForward, [
      'questionRefId',
      deletedQuestionRefId,
    ]);

    const {
      type: questionType,
      answers = [],
      scorepoints = [],
    } = currentQuestion;
    const isMatrix = isMatrixSingleMultipleQuestionType(questionType);
    const isNewMatrix = isMatrixOpenEndedType(questionType);

    const previouslyUpdatedSourceQuestion = acc.find(
      (q) => q.refId === affected.currentSourceQuestionId,
    );

    let updatedAnswers;

    if (previouslyUpdatedSourceQuestion) {
      const otherCarryForwards = reject(updatedCarryForward, [
        'questionRefId',
        previouslyUpdatedSourceQuestion.refId,
      ]);

      const sourceAnswers = previouslyUpdatedSourceQuestion.answers || [];

      const otherCarryForwardQuestionRefIds = new Set(
        otherCarryForwards.map((cf) => cf.questionRefId),
      );
      const sourceAnswerRefIds = new Set(
        sourceAnswers.map((answer) => answer.refId),
      );

      updatedAnswers = answers.filter((answer) => {
        const { originalAnswerRefId, questionRefId } = answer;

        return (
          !originalAnswerRefId ||
          otherCarryForwardQuestionRefIds.has(questionRefId) ||
          sourceAnswerRefIds.has(originalAnswerRefId)
        );
      });
    } else {
      updatedAnswers = reject(answers, [
        'questionRefId',
        affected.currentSourceQuestionId,
      ]);
    }

    const updatedQuestion = {
      ...affected.question,
      answers: updatedAnswers,
      carryForward: updatedCarryForward,
    };

    if (isMatrix) {
      updatedQuestion.scorepoints = reject(scorepoints, [
        'questionRefId',
        affected.currentSourceQuestionId,
      ]);
    }

    if (isNewMatrix) {
      const matrixAnswers = affected.matrix?.matrixAnswers || [];
      const columns = affected.matrix?.columns || [];

      const updatedMatrixAnswers = reject(matrixAnswers, [
        'questionRefId',
        affected.currentSourceQuestionId,
      ]);
      const updatedColumns = reject(columns, [
        'questionRefId',
        affected.currentSourceQuestionId,
      ]);

      updatedQuestion.matrix = {
        ...updatedQuestion.matrix,
        matrixAnswers: updatedMatrixAnswers,
        columns: updatedColumns,
        answerColumnValueTypes: getAnswerValueColumnTypesOpenEnded({
          answers: updatedAnswers,
          columns: updatedColumns,
          answerColumnValueTypes: updatedQuestion.matrix.answerColumnValueTypes,
        }),
      };
    }

    acc.push(updatedQuestion);
    return acc;
  }, []);
};

export const clearRelevantLoopAndMergeGroups = ({
  questions = [],
  deletedQuestionRefId,
}) => {
  const affectedQuestions = getAffectedLoopAndMergeGroups({
    questions,
    sourceQuestion: { refId: deletedQuestionRefId },
  });

  if (!affectedQuestions.length) return questions;

  return questions.reduce((acc, currentQuestion) => {
    const affected = affectedQuestions.find(
      (affectedQuestion) => affectedQuestion.refId === currentQuestion.refId,
    );

    if (!affected) {
      acc.push(currentQuestion);
      return acc;
    }

    const updatedQuestion =
      removeLoopAndMergeBasedOnQuestionConfiguration(affected);

    acc.push(updatedQuestion);
    return acc;
  }, []);
};

export const removeLoopAndMergeBasedOnQuestionConfiguration = (groupNode) => {
  const updatedNode = { ...groupNode };
  delete updatedNode.loopAndMergeFromQuestionConfiguration;

  updatedNode.attributes = reject(
    updatedNode.attributes,
    isLoopAndMergeAttributeBasedOnQuestion,
  );

  if (updatedNode.concepts.length === 0) {
    updatedNode.concepts = getLoopAndMergeConcepts(1);
  }
  if (updatedNode.attributes.length === 0) {
    updatedNode.attributes = getLoopAndMergeAttributes(
      1,
      updatedNode.concepts.length,
    );
  } else if (updatedNode.attributes[0].values.length === 0) {
    updatedNode.attributes = updatedNode.attributes.map((attribute) => ({
      ...attribute,
      values: [getEmptyAttributeValue()],
    }));
  }

  updatedNode.properties = {
    ...updatedNode.properties,
  };

  if (!updatedNode.properties.userDefinedRepetitions) {
    updatedNode.properties = {
      ...updatedNode.properties,
      repetitions: updatedNode.concepts.length,
    };
  }

  return updatedNode;
};

export const createCarryForward = () => ({
  refId: uuidv1(),
  questionRefId: undefined,
  answerSelection: undefined,
  answerCompareValue: [],
  answerAs: undefined,
});

export const getValidCarryForward = (carryForwardArray) =>
  reject(carryForwardArray, (carryForward) =>
    isNotValidForBackend(carryForward),
  );

export const createCarriedAnswer = ({
  questionRefId,
  answer,
  isFromScorepoint,
  targetType,
}) => {
  const carriedDetails = {
    questionRefId,
    isCarriedForward: true,
  };
  if (isFromScorepoint) {
    carriedDetails.originalScorepointRefId = answer.refId;
  } else {
    carriedDetails.originalAnswerRefId = answer.refId;
  }

  return updateCarriedAnswer(
    {
      ...getEmptySingleMultipleAnswer(true),
      ...carriedDetails,
    },
    answer,
    targetType,
  );
};

export const updateCarriedAnswer = (oldAnswer, newAnswer, targetType) => {
  const canHaveMedia = questionAnswerSupportsMedia(targetType);
  return {
    ...oldAnswer,
    body: newAnswer.body,
    media: canHaveMedia ? newAnswer.media : undefined,
    hasImage: canHaveMedia ? Boolean(newAnswer.media) : false,
  };
};

export const isNotValidForBackend = ({
  questionRefId,
  answerSelection,
  answerAs,
}) => !questionRefId || !answerSelection || !answerAs;

export const isIncompleteCarryForward = (
  { questionRefId, answerSelection, answerAs, answerCompareValue },
  questions,
) => {
  const question = find(questions, ['refId', questionRefId]) || {};
  const { type: questionType } = question;

  const isSingleOrMultiple = isSingleOrMultipleQuestionType(questionType);
  const isRanking = isRankingQuestionType(questionType);
  const isMatrix = isMatrixSingleMultipleQuestionType(questionType);
  const isBipolar = isBipolarQuestionType(questionType);
  if (isBipolar) {
    if (
      answerSelection !== ANSWER_OPTIONS.MATRIX_ALL_ROWS &&
      answerSelection !== ANSWER_OPTIONS.MATRIX_HIGHEST_VALUE &&
      answerSelection !== ANSWER_OPTIONS.MATRIX_LOWEST_VALUE &&
      isEmpty(answerCompareValue)
    ) {
      return true;
    }
  }

  return (
    !questionRefId ||
    !answerSelection ||
    !answerAs ||
    (isSingleOrMultiple &&
      [
        ANSWER_OPTIONS.SPECIFIC_SELECTED_VALUES,
        ANSWER_OPTIONS.SPECIFIC_UNSELECTED_VALUES,
      ].includes(answerSelection) &&
      isEmpty(answerCompareValue)) ||
    (isRanking &&
      [
        ANSWER_OPTIONS.LESSER_THAN,
        ANSWER_OPTIONS.GREATER_THAN,
        ANSWER_OPTIONS.EQUAL_TO,
      ].includes(answerSelection) &&
      isEmpty(answerCompareValue)) ||
    (isMatrix &&
      [
        ANSWER_OPTIONS.SELECTED_FOR_ROW,
        ANSWER_OPTIONS.UNSELECTED_FOR_ROW,
        ANSWER_OPTIONS.SELECTED_FOR_COLUMN,
        ANSWER_OPTIONS.UNSELECTED_FOR_COLUMN,
        ANSWER_OPTIONS.SPECIFIC_SELECTED_COLUMNS,
        ANSWER_OPTIONS.SPECIFIC_UNSELECTED_COLUMNS,
      ].includes(answerSelection) &&
      isEmpty(answerCompareValue))
  );
};

export const hasIncompleteCarryForward = (carryForwardArray = [], questions) =>
  carryForwardArray.some((carryForward) =>
    isIncompleteCarryForward(carryForward, questions),
  );

export const removeAllCarryForward = (question) => {
  const { type: questionType, answers, scorepoints, matrix } = question;
  const isMatrix = isMatrixSingleMultipleQuestionType(questionType);
  const updatedAnswers = reject(answers, 'isCarriedForward');
  const updatedQuestion = {
    ...question,
    carryForward: [],
    answers: updatedAnswers,
  };
  if (isMatrix) {
    updatedQuestion.scorepoints = reject(scorepoints, 'isCarriedForward');
  }
  if (!isEmpty(matrix)) {
    let updatedAnswersOrColumns = false;
    const newMatrixProps = { ...matrix };
    if (!isEmpty(newMatrixProps.matrixAnswers)) {
      updatedAnswersOrColumns = true;
      newMatrixProps.matrixAnswers = reject(
        newMatrixProps.matrixAnswers,
        'isCarriedForward',
      );
    }
    if (!isEmpty(newMatrixProps.columns)) {
      updatedAnswersOrColumns = true;
      newMatrixProps.columns = reject(
        newMatrixProps.columns,
        'isCarriedForward',
      );
    }
    if (updatedAnswersOrColumns) {
      newMatrixProps.answerColumnValueTypes =
        getAnswerValueColumnTypesOpenEnded({
          answers: updatedQuestion.answers,
          columns: newMatrixProps.columns,
          answerColumnValueTypes: newMatrixProps.answerColumnValueTypes,
        });
    }
    updatedQuestion.matrix = newMatrixProps;
  }
  return updatedQuestion;
};

// Keep in mind that for each answer selection options
// of a matrix bipolar question all the statements
// may be carried forwarded
export const getAllCarriedForwardCount = (
  carryForwardArray = [],
  questions,
) => {
  const minMaxes = carryForwardArray.map((carryForwardDetails) => {
    const forwardedQuestion = find(questions, [
      'refId',
      carryForwardDetails.questionRefId,
    ]);
    const counts = getCarriedForwardCount(
      forwardedQuestion,
      carryForwardDetails.answerSelection,
      carryForwardDetails.answerCompareValue,
    );
    return {
      answerAs: carryForwardDetails.answerAs,
      counts,
    };
  });
  return minMaxes.reduce(
    (accumulator, { answerAs, counts: { min = 0, max = 0 } }) => {
      const typeCounts = accumulator[answerAs] || { min: 0, max: 0 };
      typeCounts.min += min;
      typeCounts.max += max;
      accumulator[answerAs] = typeCounts;

      return accumulator;
    },
    {},
  );
};

export const getCarriedForwardCount = (
  sourceQuestion = {},
  answerSelection,
  answerCompareValue,
) => {
  const {
    type: questionType,
    answers = [],
    scorepoints = [],
    hasNodeDisplayLogic,
  } = sourceQuestion;
  let minToCarry;
  let maxToCarry;

  if (hasNodeDisplayLogic) {
    return { min: 0, max: 0 };
  }

  switch (questionType) {
    case QUESTION_SINGLE:
    case QUESTION_SINGLE_PEEQ: {
      const answersLength = answers.length;
      if (ANSWER_OPTIONS.ALL_VALUES === answerSelection) {
        minToCarry = answersLength;
        maxToCarry = answersLength;
        break;
      }
      if (ANSWER_OPTIONS.UNSELECTED_VALUES === answerSelection) {
        minToCarry = answersLength - 1;
        maxToCarry = answersLength - 1;
        break;
      }
      if (ANSWER_OPTIONS.SPECIFIC_SELECTED_VALUES === answerSelection) {
        const isSame = sameLength(answerCompareValue, answers);
        minToCarry = isSame ? 1 : 0;
        maxToCarry = 1;
        break;
      }
      if (ANSWER_OPTIONS.SPECIFIC_UNSELECTED_VALUES === answerSelection) {
        const isSame = sameLength(answerCompareValue, answers);
        minToCarry = isSame ? answersLength - 1 : 0;
        maxToCarry = isSame ? answersLength - 1 : answerCompareValue.length;
        break;
      }
      if (ANSWER_OPTIONS.VISIBLE_VALUES === answerSelection) {
        minToCarry = 1;
        maxToCarry = answersLength;
        break;
      }
      if (ANSWER_OPTIONS.HIDDEN_VALUES === answerSelection) {
        minToCarry = 0;
        maxToCarry = answersLength - 1;
        break;
      }
      // otherwise SELECTED_VALUES
      minToCarry = 1;
      maxToCarry = 1;
      break;
    }
    case QUESTION_MULTIPLE:
    case QUESTION_MULTIPLE_PEEQ: {
      const answersLength = answers.length;
      if (ANSWER_OPTIONS.ALL_VALUES === answerSelection) {
        minToCarry = answersLength;
        maxToCarry = answersLength;
        break;
      }
      const { min, max } = sourceQuestion;
      const hasNoneOfTheAbove = answers.find(
        ({ type }) => type === ANSWER_NONE_OF_THE_ABOVE,
      );
      // initialize to the SELECTED_VALUES
      minToCarry = hasNoneOfTheAbove ? 1 : min || 1;
      maxToCarry =
        max || (hasNoneOfTheAbove ? answersLength - 1 : answersLength);
      if (answerSelection === ANSWER_OPTIONS.UNSELECTED_VALUES) {
        [minToCarry, maxToCarry] = [
          answersLength - maxToCarry,
          answersLength - minToCarry,
        ];
        break;
      }
      if (ANSWER_OPTIONS.SPECIFIC_SELECTED_VALUES === answerSelection) {
        const isSame = sameLength(answerCompareValue, answers);
        if (!isSame) {
          const answerCompareLength = answerCompareValue.length;
          minToCarry = 0;
          maxToCarry =
            maxToCarry > answerCompareLength ? answerCompareLength : maxToCarry;
          break;
        }
        // when the answerCompareValue is the same length as the answers
        // then minToCarry add maxToCarry are the same as SELECTED_VALUES
        break;
      }
      if (ANSWER_OPTIONS.SPECIFIC_UNSELECTED_VALUES === answerSelection) {
        const isSame = sameLength(answerCompareValue, answers);
        if (!isSame) {
          const answerCompareLength = answerCompareValue.length;
          const answersLengthMinusMinToCarry = answersLength - minToCarry;
          minToCarry = 0;
          maxToCarry =
            answersLengthMinusMinToCarry > answerCompareLength
              ? answerCompareLength
              : answersLengthMinusMinToCarry;
          break;
        }
        // when the answerCompareValue is the same length as the answers
        // then minToCarry add maxToCarry are the same as UNSELECTED_VALUES
        [minToCarry, maxToCarry] = [
          answersLength - maxToCarry,
          answersLength - minToCarry,
        ];
        break;
      }
      if (ANSWER_OPTIONS.VISIBLE_VALUES === answerSelection) {
        minToCarry = 1;
        // eslint-disable-next-line no-self-assign
        maxToCarry = maxToCarry;
        break;
      }
      if (ANSWER_OPTIONS.HIDDEN_VALUES === answerSelection) {
        [minToCarry, maxToCarry] = [0, answersLength - minToCarry];
        break;
      }
      break;
    }
    case QUESTION_NUMERIC_OPEN_ENDED:
    case QUESTION_OPEN_ENDED:
    case QUESTION_SLIDER: {
      minToCarry = 1;
      maxToCarry = 1;
      break;
    }
    case QUESTION_RANKING: {
      const answersLength = answers.length;
      if (ANSWER_OPTIONS.ALL_VALUES === answerSelection) {
        minToCarry = answersLength;
        maxToCarry = answersLength;
        break;
      }
      if (ANSWER_OPTIONS.LESSER_THAN === answerSelection) {
        minToCarry = Number(answerCompareValue[0]) - 1;
        maxToCarry = Number(answerCompareValue[0]) - 1;
        break;
      }
      if (ANSWER_OPTIONS.GREATER_THAN === answerSelection) {
        minToCarry = answersLength - Number(answerCompareValue[0]);
        maxToCarry = answersLength - Number(answerCompareValue[0]);
        break;
      }
      if (ANSWER_OPTIONS.VISIBLE_VALUES === answerSelection) {
        minToCarry = 1;
        maxToCarry = answersLength;
        break;
      }
      if (ANSWER_OPTIONS.HIDDEN_VALUES === answerSelection) {
        minToCarry = 0;
        maxToCarry = answersLength - 1;
        break;
      }
      // otherwise
      // ANSWER_OPTIONS.HIGHEST_VALUE, ANSWER_OPTIONS.LOWEST_VALUE, ANSWER_OPTIONS.EQUAL_TO
      minToCarry = 1;
      maxToCarry = 1;
      break;
    }
    case QUESTION_MATRIX_SINGLE: {
      const answersLength = answers.length;
      const scorepointsLength = scorepoints.length;
      switch (answerSelection) {
        case ANSWER_OPTIONS.ALL_ROWS:
          minToCarry = answersLength;
          maxToCarry = answersLength;
          break;
        case ANSWER_OPTIONS.ALL_COLUMNS:
          minToCarry = scorepointsLength;
          maxToCarry = scorepointsLength;
          break;
        case ANSWER_OPTIONS.SELECTED_COLUMNS:
          minToCarry = 1;
          maxToCarry = scorepointsLength;
          break;
        case ANSWER_OPTIONS.SELECTED_FOR_ROW:
          minToCarry = 1;
          maxToCarry = answerCompareValue.length || 1;
          break;
        case ANSWER_OPTIONS.SELECTED_FOR_COLUMN:
        case ANSWER_OPTIONS.UNSELECTED_FOR_COLUMN:
          minToCarry = 0;
          maxToCarry = answersLength;
          break;
        case ANSWER_OPTIONS.UNSELECTED_COLUMNS:
          minToCarry = 0;
          maxToCarry = scorepointsLength - 1;
          break;
        case ANSWER_OPTIONS.UNSELECTED_FOR_ROW:
          minToCarry = scorepointsLength - (answerCompareValue.length || 1);
          maxToCarry = scorepointsLength - 1;
          break;
        case ANSWER_OPTIONS.SPECIFIC_SELECTED_COLUMNS:
        case ANSWER_OPTIONS.SPECIFIC_UNSELECTED_COLUMNS:
          minToCarry = 0;
          maxToCarry = answerCompareValue.length;
          break;
        case ANSWER_OPTIONS.VISIBLE_ROWS:
          minToCarry = 1;
          maxToCarry = answersLength;
          break;
        case ANSWER_OPTIONS.HIDDEN_ROWS:
          minToCarry = 0;
          maxToCarry = answersLength - 1;
          break;
        case ANSWER_OPTIONS.VISIBLE_COLUMNS:
          minToCarry = 1;
          maxToCarry = scorepointsLength;
          break;
        case ANSWER_OPTIONS.HIDDEN_COLUMNS:
          minToCarry = 0;
          maxToCarry = scorepointsLength - 1;
          break;
        default:
          break;
      }
      break;
    }
    case QUESTION_MATRIX_MULTIPLE: {
      const answersLength = answers.length;
      const scorepointsLength = scorepoints.length;
      switch (answerSelection) {
        case ANSWER_OPTIONS.ALL_ROWS:
          minToCarry = answersLength;
          maxToCarry = answersLength;
          break;
        case ANSWER_OPTIONS.ALL_COLUMNS:
          minToCarry = scorepointsLength;
          maxToCarry = scorepointsLength;
          break;
        case ANSWER_OPTIONS.SELECTED_COLUMNS:
        case ANSWER_OPTIONS.SELECTED_FOR_ROW:
          minToCarry = 1;
          maxToCarry = scorepointsLength;
          break;
        case ANSWER_OPTIONS.SELECTED_FOR_COLUMN:
        case ANSWER_OPTIONS.UNSELECTED_FOR_COLUMN:
          minToCarry = 0;
          maxToCarry = answersLength;
          break;
        case ANSWER_OPTIONS.UNSELECTED_COLUMNS:
        case ANSWER_OPTIONS.UNSELECTED_FOR_ROW:
          minToCarry = 0;
          maxToCarry = scorepointsLength - 1;
          break;
        case ANSWER_OPTIONS.SPECIFIC_SELECTED_COLUMNS:
        case ANSWER_OPTIONS.SPECIFIC_UNSELECTED_COLUMNS:
          minToCarry = 0;
          maxToCarry = answerCompareValue.length;
          break;
        case ANSWER_OPTIONS.VISIBLE_ROWS:
          minToCarry = 1;
          maxToCarry = answersLength;
          break;
        case ANSWER_OPTIONS.HIDDEN_ROWS:
          minToCarry = 0;
          maxToCarry = answersLength - 1;
          break;
        case ANSWER_OPTIONS.VISIBLE_COLUMNS:
          minToCarry = 1;
          maxToCarry = scorepointsLength;
          break;
        case ANSWER_OPTIONS.HIDDEN_COLUMNS:
          minToCarry = 0;
          maxToCarry = scorepointsLength - 1;
          break;
        default:
          break;
      }
      break;
    }
    case QUESTION_MATRIX_BIPOLAR: {
      const { columns = [] } = sourceQuestion?.matrix || {};
      const isSameLength = sameLength(answerCompareValue, columns);
      const answersLength = answers.length;

      switch (answerSelection) {
        case ANSWER_OPTIONS.MATRIX_ALL_ROWS:
          minToCarry = answersLength;
          maxToCarry = answersLength;
          break;
        case ANSWER_OPTIONS.MATRIX_EQUAL:
          minToCarry = isSameLength ? answersLength : 0;
          maxToCarry = answersLength;
          break;
        case ANSWER_OPTIONS.MATRIX_HIGHEST_VALUE:
        case ANSWER_OPTIONS.MATRIX_LOWEST_VALUE:
          minToCarry = 1;
          maxToCarry = answersLength;
          break;
        case ANSWER_OPTIONS.MATRIX_GREATER_THAN:
        case ANSWER_OPTIONS.MATRIX_LESSER_THAN:
        case ANSWER_OPTIONS.MATRIX_EQUAL_GREATER_THAN:
        case ANSWER_OPTIONS.MATRIX_EQUAL_LESSER_THAN:
          minToCarry = 0;
          maxToCarry = answersLength;
          break;
        case ANSWER_OPTIONS.MATRIX_NOT_EQUAL:
          minToCarry = 0;
          maxToCarry = isSameLength ? 0 : answersLength;
          break;
        default:
          break;
      }
      break;
    }
    default:
      break;
  }

  return { min: minToCarry || 0, max: maxToCarry || 0 };
};

export const applyCarriedQuestionAnswersToMatrix = ({
  previousCarryForward,
  forwardedAnswers,
  question,
  forwardedDetails,
  questions,
}) => {
  const {
    answerAs,
    questionRefId: currentQuestionRefId,
    answerSelection,
    answerLabelSelection,
    refId: configurationRefId,
  } = forwardedDetails;

  const previousSetting = find(previousCarryForward, [
    'refId',
    configurationRefId,
  ]);
  const { questionRefId: previousQuestionRefId } = previousSetting;

  const sourceQuestion = find(questions, ['refId', currentQuestionRefId]) || {};
  const { type: sourceType } = sourceQuestion;

  const { answers: targetAnswers = [], matrix = {} } = question;
  const {
    columns: targetColumns = [],
    matrixAnswers: targetMatrixAnswers = [],
  } = matrix;

  if (answerAs === FORWARD_ANSWER_AS.ANSWERS) {
    let targetAnswersToKeep = targetAnswers;
    let targetMatrixAnswersToKeep = targetMatrixAnswers;

    if (currentQuestionRefId !== previousQuestionRefId) {
      targetAnswersToKeep = reject(targetAnswersToKeep, [
        'questionRefId',
        previousQuestionRefId,
      ]);
      targetMatrixAnswersToKeep = reject(targetMatrixAnswersToKeep, [
        'questionRefId',
        previousQuestionRefId,
      ]);
    }

    const { updatedAnswers, updatedMatrixAnswers } =
      upsertDeleteTargetFromSource({
        fromItems: forwardedAnswers,
        toItems: targetAnswersToKeep,
        toItemsMatrixAnswers: targetMatrixAnswersToKeep,
        sourceRefId: currentQuestionRefId,
        sourceType,
        answerSelection,
        answerLabelSelection,
        targetType: question.type,
        changedProperty: MATRIX_ANSWER_PROP,
      });

    const updatedColumns = reject(targetColumns, [
      'questionRefId',
      previousQuestionRefId,
    ]);

    return {
      answers: updatedAnswers,
      columns: updatedColumns,
      matrixAnswers: updatedMatrixAnswers,
    };
  }
  if (answerAs === FORWARD_ANSWER_AS.SCOREPOINTS) {
    let targetColumnsToKeep = targetColumns;

    if (currentQuestionRefId !== previousQuestionRefId) {
      targetColumnsToKeep = reject(targetColumnsToKeep, [
        'originalQuestionRefId',
        previousQuestionRefId,
      ]);
    }

    const { updatedMatrixAnswers: updatedColumns } =
      upsertDeleteTargetFromSource({
        fromItems: forwardedAnswers,
        toItemsMatrixAnswers: targetColumnsToKeep,
        sourceRefId: currentQuestionRefId,
        sourceType,
        answerSelection,
        answerLabelSelection,
        targetType: question.type,
        changedProperty: MATRIX_COLUMN_PROP,
      });
    const updatedAnswers = reject(targetAnswers, [
      'questionRefId',
      previousQuestionRefId,
    ]);
    const updatedMatrixAnswers = reject(targetMatrixAnswers, [
      'questionRefId',
      previousQuestionRefId,
    ]);

    return {
      answers: updatedAnswers,
      matrixAnswers: updatedMatrixAnswers,
      columns: updatedColumns,
    };
  }

  return {
    answers: targetAnswers,
    columns: targetColumns,
    matrixAnswers: targetMatrixAnswers,
  };
};

export const isQuestionCarryForwarded = (refId, questions) =>
  some(questions, ({ carryForward = [] }) =>
    some(carryForward, ['questionRefId', refId]),
  );

export const isQuestionUsedInLoopAndMerge = (
  refId,
  nodes = [],
  predicateFn = ({ loopAndMergeFromQuestionConfiguration = {} }) =>
    loopAndMergeFromQuestionConfiguration.questionRefId === refId,
) =>
  nodes
    .filter(({ type, groupType }) => type ?? groupType === GROUP_LOOP_MERGE)
    .some(predicateFn);

export const getLoopAndMergeGroupWithMergedNode = (
  refId,
  nodes = [],
  predicateFn = (node) =>
    (node.groupType ?? node.type) === GROUP_LOOP_MERGE &&
    node.attributeSet?.loopAndMerge?.mergeQuestionRefId === refId,
) => nodes.find(predicateFn);

const sameLength = (a, b) => a.length === b.length;

export const onlyAnswerSelectionWithPossibleZeroCarryForward = (
  carryForward = [],
) => {
  if (isEmpty(carryForward)) {
    return false;
  }
  return every(carryForward, ({ answerSelection }) =>
    [
      ANSWER_OPTIONS.SPECIFIC_SELECTED_VALUES,
      ANSWER_OPTIONS.SPECIFIC_UNSELECTED_VALUES,
      ANSWER_OPTIONS.SPECIFIC_SELECTED_COLUMNS,
      ANSWER_OPTIONS.SPECIFIC_UNSELECTED_COLUMNS,
      ANSWER_OPTIONS.HIDDEN_VALUES,
      ANSWER_OPTIONS.HIDDEN_ROWS,
      ANSWER_OPTIONS.HIDDEN_COLUMNS,
    ].includes(answerSelection),
  );
};

export const matrixToMatrixUnselectedCarryForward = ({
  targetQuestion,
  allNodes,
}) => {
  const isTargetMatrix = isMatrixSingleMultipleQuestionType(
    targetQuestion.type,
  );

  if (!isTargetMatrix) {
    return false;
  }

  const { carryForward = [] } = targetQuestion;
  if (isEmpty(carryForward)) {
    return false;
  }

  return every(carryForward, ({ answerSelection, questionRefId }) => {
    const sourceQuestion = allNodes.find(
      ({ refId }) => refId === questionRefId,
    );

    if (!sourceQuestion) {
      return false;
    }

    const isSourceMatrix = isMatrixSingleMultipleQuestionType(
      sourceQuestion.type,
    );

    if (
      isMatrixMultipleQuestionType(sourceQuestion.type) &&
      ANSWER_OPTIONS.UNSELECTED_FOR_ROW === answerSelection
    ) {
      return true;
    }

    if (isSourceMatrix) {
      return [ANSWER_OPTIONS.UNSELECTED_FOR_COLUMN].includes(answerSelection);
    }
    return false;
  });
};

export const getSourceCleanedCarryForward = ({
  answerSelection,
  answerCompareValue,
  answers,
}) => {
  const isSpecificAnswerSelection =
    ANSWER_OPTIONS.SPECIFIC_SELECTED_VALUES === answerSelection ||
    ANSWER_OPTIONS.SPECIFIC_UNSELECTED_VALUES === answerSelection ||
    ANSWER_OPTIONS.SPECIFIC_SELECTED_COLUMNS === answerSelection ||
    ANSWER_OPTIONS.SPECIFIC_UNSELECTED_COLUMNS === answerSelection;

  let cleanedAnswers = answers;
  if (isSpecificAnswerSelection && cleanedAnswers?.length > 0) {
    cleanedAnswers = cleanedAnswers.filter(({ refId }) =>
      answerCompareValue.includes(refId),
    );
  }
  return cleanedAnswers;
};

export function findCarryForwardRootPath({
  questionRefId,
  nodes,
  paths = [],
  visited = new Set(),
}) {
  const question = nodes.find((q) => q.refId === questionRefId);
  if (!question) return [];

  const pathsUpdated = paths.concat(question);

  if (!question.carryForward || question.carryForward.length === 0) {
    // This is a root question, return the paths that lead to it
    // Exclude the start question with slice(1)
    return [pathsUpdated.slice(1)];
  }

  let rootPaths = [];
  question.carryForward.forEach((carry) => {
    if (!visited.has(carry.questionRefId)) {
      visited.add(carry.questionRefId);
      const potentialPaths = findCarryForwardRootPath({
        questionRefId: carry.questionRefId,
        nodes,
        paths: pathsUpdated,
        visited,
      });
      rootPaths = [...rootPaths, ...potentialPaths];
    }
  });

  return rootPaths;
}

export function getAvailableCarryForwardQuestions({
  availableQuestionsForCarryForward,
  allQuestions,
  carryForwardArray,
  currentCarryForwardQuestionRefId,
  currentQuestionRefId,
}) {
  const availableQuestionsToSelect = differenceWith(
    availableQuestionsForCarryForward,
    carryForwardArray,
    (question, fwd) =>
      question.refId === fwd.questionRefId &&
      question.refId !== currentCarryForwardQuestionRefId,
  );

  const currentQuestionIndex = allQuestions.findIndex(
    (question) => question.refId === currentQuestionRefId,
  );

  const previousQuestions = allQuestions.slice(0, currentQuestionIndex + 1);

  const cfSiblings = carryForwardArray.filter(
    (cf) => cf.questionRefId !== currentCarryForwardQuestionRefId,
  );

  let shouldExclude = cfSiblings
    .map((sibling) => {
      if (!sibling.questionRefId) return [];

      return findCarryForwardRootPath({
        questionRefId: sibling.questionRefId,
        nodes: previousQuestions,
      });
    })
    .flat(2);

  shouldExclude = uniqBy(shouldExclude, 'refId');

  const pathsOfPreviousQuestions = previousQuestions.flatMap((question) =>
    findCarryForwardRootPath({
      questionRefId: question.refId,
      nodes: previousQuestions,
    }),
  );

  const affectedPathsFromSiblings = [];

  previousQuestions.forEach((question, idx2) => {
    if (!pathsOfPreviousQuestions[idx2]) return;
    const newPath = pathsOfPreviousQuestions[idx2].map(
      ({ refId: refId2 }) => refId2,
    );

    shouldExclude.forEach((shouldExcludeQuestion) => {
      if (newPath.includes(shouldExcludeQuestion.refId)) {
        affectedPathsFromSiblings.push(question);
      }
    });
  });

  const currentQuestion = allQuestions[currentQuestionIndex];

  const selfAffectedQuestions = getAffectedCarryForwardQuestions({
    questions: allQuestions,
    sourceQuestion: currentQuestion,
  }).map((affected) => affected.question);

  const siblingsAffectedQuestions = cfSiblings
    .flatMap((sibling) => {
      if (!sibling.questionRefId) return [];

      return getAffectedCarryForwardQuestions({
        questions: allQuestions,
        sourceQuestion: allQuestions.find(
          (question) => question.refId === sibling.questionRefId,
        ),
      });
    })
    .map((affected) => affected.question);

  const selfAffectedQuestionsPaths = selfAffectedQuestions.flatMap((question) =>
    findCarryForwardRootPath({
      questionRefId: question.refId,
      nodes: allQuestions,
    }),
  );

  // selfAffectedQuestionsPaths without current paths
  const cleanSelfAffectedQuestionsPaths = [];

  selfAffectedQuestionsPaths.forEach((path) => {
    if (path.length < 1) return;

    if (path[0].refId === currentQuestion.refId) return;
    cleanSelfAffectedQuestionsPaths.push(path);
  });

  let questionsToSelectPaths = {};

  availableQuestionsToSelect.forEach((question) => {
    const questionsPaths = findCarryForwardRootPath({
      questionRefId: question.refId,
      nodes: allQuestions,
    }).flat();

    if (questionsPaths.length < 1) return;

    questionsToSelectPaths = {
      ...questionsToSelectPaths,
      [question.refId]: questionsPaths,
    };
  });

  const pathsThatAffectNextQuestions = [];

  selfAffectedQuestionsPaths.flat(2).forEach((question) => {
    Object.keys(questionsToSelectPaths).forEach((key) => {
      questionsToSelectPaths[key].forEach((value) => {
        if (question.refId === value.refId) {
          const questionToPush = allQuestions.find(
            (questionToFind) => questionToFind.refId === key,
          );
          pathsThatAffectNextQuestions.push(questionToPush);
        }
      });
    });
  });

  return differenceWith(
    availableQuestionsToSelect,
    uniqBy(
      [
        ...shouldExclude,
        ...affectedPathsFromSiblings,
        ...cleanSelfAffectedQuestionsPaths.flat(),
        ...pathsThatAffectNextQuestions,
        ...siblingsAffectedQuestions,
      ],
      'refId',
    ),
    (question, exclude) => question.refId === exclude.refId,
  );
}
